<script setup lang="ts">
  import { onMounted, type Ref } from 'vue'
  import type { ProgressInject } from '@/types/ui'

  import { ref, computed, provide, readonly, watch, onBeforeMount } from 'vue'

  import { useRoute } from 'vue-router'
  import { syncRef } from '@vueuse/core'

  import { useHead } from '@unhead/vue'
  import { useSchemaOrg, defineWebSite } from '@unhead/schema-org'

  import { useNProgress } from '@vueuse/integrations/useNProgress'
  import '@/assets/nprogress.css'

  import { storeToRefs } from 'pinia'
  import { useUiStore } from '@/stores/ui'

  import { useAuth } from '@/composables/useAuth'
  import { useBannerBlockImagePreloader } from '@/composables/useBannerBlockImagePreloader'
  import { useSeo } from '@/composables/useSeo'

  import { MANAGER_TAB_TREE, MANAGER_TITLE_LIST } from '@/constants'

  const { isLoading } = useNProgress(null, {
    template:
      '<div class="bar !z-[9999]" role="bar"><div class="peg"></div></div>',
  })
  const setLoading = (loading: Ref<boolean>) =>
    syncRef(isLoading, loading, { direction: 'rtl' })

  const progress: ProgressInject = {
    isLoading: readonly(isLoading),
    setLoading,
  }
  provide('progress', progress)

  const route = useRoute()

  const { authorized } = useAuth(route)
  const {
    staticPages,
    metasStore,
    isManager,
    routeName,
    isNotDynamicPage,
    staticPageMeta,
  } = useSeo(route)

  onBeforeMount(() => {
    if (!staticPages.value.length) metasStore.fetchStaticPagesC()
  })

  useBannerBlockImagePreloader()

  const title = computed(() => {
    const site = 'SEEN.'
    let title = ''

    if (isManager.value) {
      const header = MANAGER_TITLE_LIST[routeName.value]?.title
      const tab = MANAGER_TAB_TREE[routeName.value]?.[routeName.value]?.name

      title =
        header && tab && header !== tab
          ? `${header}: ${tab.toLowerCase()}`
          : tab ?? header
    } else if (isNotDynamicPage.value) {
      return staticPageMeta.value?.title ?? site
    }

    return title ? `${title} — ${site}` : site
  })

  const keywords = computed(() => {
    if (isManager.value) return ''

    if (isNotDynamicPage.value) {
      return staticPageMeta.value?.keywords ?? ''
    }

    return ''
  })

  const description = computed(() => {
    if (isManager.value) return ''

    if (isNotDynamicPage.value) {
      return staticPageMeta.value?.description ?? ''
    }

    return ''
  })

  const googleScript = computed(() => {
    let env = import.meta.env.DEV ? 'development' : 'production'
    if (import.meta.env.MODE) env = import.meta.env.MODE

    // if (env !== 'production' || isManager.value) return ''

    return `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.defer=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','GTM-5SMSX47F');`
  })
  const mailCounterScript = computed(() => {
    let env = import.meta.env.DEV ? 'development' : 'production'
    if (import.meta.env.MODE) env = import.meta.env.MODE

    // if (env !== 'production' || isManager.value) return ''

    return `var _tmr = window._tmr || (window._tmr = []);
          _tmr.push({id: "3391308", type: "pageView", start: (new Date()).getTime()});
          (function (d, w, id) {
            if (d.getElementById(id)) return;
            var ts = d.createElement("script"); ts.type = "text/javascript"; ts.async = true; ts.id = id;
            ts.src = "https://top-fwz1.mail.ru/js/code.js";
            var f = function () {var s = d.getElementsByTagName("script")[0]; s.parentNode.insertBefore(ts, s);};
            if (w.opera == "[object Opera]") { d.addEventListener("DOMContentLoaded", f, false); } else { f(); }
          })(document, window, "tmr-code");`
  })

  function loadNoSCriptMailCounter() {
    if (document.getElementById('noscript-pixel')) return

    const html = `<noscript id="noscript-pixel"><div><img src="https://top-fwz1.mail.ru/counter?id=3391308;js=na" style="position:absolute;left:-9999px;" alt="Top.Mail.Ru" /></div></noscript>`

    document.body.insertAdjacentHTML('afterbegin', html)
  }

  const googleNoScriptAdded = ref(false)

  watch(
    () => isManager.value,
    () => {
      let env = import.meta.env.DEV ? 'development' : 'production'
      if (import.meta.env.MODE) env = import.meta.env.MODE

      // if (env !== 'production' || isManager.value || googleNoScriptAdded.value)
      // return

      const html = `<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5SMSX47F" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>`

      if (document.body) {
        document.body.insertAdjacentHTML('afterbegin', html)
        googleNoScriptAdded.value = true
      }
    },
    { immediate: true }
  )
  /**
   * Captcha
   * */
  const { captchaOptions } = storeToRefs(useUiStore())

  function onloadFunction() {
    const captchaKey = (import.meta.env.VITE_CAPTCHA_KEY ?? '') as string
    if (!captchaKey) return

    window.smartCaptcha?.render('captcha-container', {
      sitekey: captchaKey,
      invisible: true,
      hideShield: true,
      callback: (token: string) => {
        if (!captchaOptions.value.fn) return

        captchaOptions.value.fn(token)

        captchaOptions.value.location = null
        captchaOptions.value.fn = null

        window.smartCaptcha.reset()
      },
    })
  }
  window.onloadFunction = onloadFunction
  ////
  const host = (import.meta.env.VITE_HOST ?? '') as string
  const base = (import.meta.env.VITE_BASE ?? '') as string
  const logoPath = `${base}logo.jpg`
  /**
   * В динамичных страницах витрины (post / catalog (brand) / product)
   * мета страницы повторно назначается из самих вьюх, т.к. она приходит
   * внутри конкретной сущности
   * */
  useHead({
    title,
    link: [
      { rel: 'canonical', href: () => window.location.href },
      {
        rel: 'preconnect',
        href: () => {
          let env = import.meta.env.DEV ? 'development' : 'production'
          if (import.meta.env.MODE) env = import.meta.env.MODE

          if (env !== 'production' || isManager.value) return ''

          return 'https://www.googletagmanager.com'
        },
      },
      {
        rel: 'preconnect',
        href: (import.meta.env.VITE_API_HOST ?? '') as string,
      },
    ],
    script: [
      { innerHTML: googleScript },
      { innerHTML: mailCounterScript, type: 'text/javascript', async: true },
      {
        src: () => {
          const captchaKey = (import.meta.env.VITE_CAPTCHA_KEY ?? '') as string

          if (isManager.value || !captchaKey) return ''

          return 'https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=onloadFunction'
        },
        defer: true,
      },
    ],
    meta: [
      {
        name: 'keywords',
        content: keywords,
      },
      {
        name: 'description',
        content: description,
      },
      {
        property: 'og:type',
        content: 'website',
      },
      {
        property: 'og:url',
        content: () => window.location.href,
      },
      {
        property: 'og:title',
        content: title,
      },
      {
        property: 'og:description',
        content: description,
      },
      {
        property: 'og:image',
        content: logoPath,
      },
    ],
    templateParams: {
      schemaOrg: {
        host,
      },
    },
  })

  useSchemaOrg([
    defineWebSite({
      '@type': 'Store',
      name: 'Интернет-магазин солнцезащитных очков и оптических оправ SEEN',
      url: base,
      logo: logoPath,
      description:
        'Закажите Солнцезащитные очки и оправы с примеркой в нашем салоне оптики SEEN в Москве, или купите понравившиеся очки в интернет-магазине с доставкой.',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Новинский бульвар, 28/35',
        addressLocality: 'Москва',
        addressCountry: 'Россия',
      },
      openingHours: 'Mo, Tu, We, Th, Fr, Sa, Su 11:00-21:00',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+7 495 109 59 39',
        email: 'info@seenstore.ru',
      },
    }),
  ])

  const layout = computed(() => {
    // if (window.location.pathname === '/') return 'TheCustomerLayout'

    return route?.meta?.layout ?? 'div'
  })

  const hideLayout = computed(
    () => !authorized.value && layout.value === 'TheManagerLayout'
  )

  watch(
    () => hideLayout.value,
    () => {
      setLoading(hideLayout)
    }
  )

  onMounted(() => {
    // loadNoSCriptMailCounter()
  })
</script>

<script lang="ts">
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'TheAppLayout',
  })
</script>

<template>
  <div id="captcha-container" />

  <component :is="layout" v-if="!hideLayout">
    <router-view />
  </component>
</template>
